import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Modal shown globally when the user tries to dispatch a job but their access
 * is blocked (trial expired, or trial-spend cap reached). Driven by AuthContext
 * `isGateModalOpen`; tools open it via `setGateModalOpen(true)` instead of
 * calling `alert()`.
 */
const AccessBlockedModal: React.FC = () => {
  const navigate = useNavigate();
  const { isGateModalOpen, setGateModalOpen, trialState } = useAuth();

  const reason = trialState?.blockedReason;
  const status = trialState?.status;

  const title =
    reason === 'trial_expired' || status === 'trial_expired'
      ? 'Your free trial has ended'
      : reason === 'usage_limit'
        ? "You've used your $15 trial credit"
        : 'Subscription required';

  const body =
    reason === 'trial_expired' || status === 'trial_expired'
      ? 'Your 14-day free trial of AI Scribe is over. Choose a plan to keep using the tools.'
      : reason === 'usage_limit'
        ? 'You hit the $15 cap on your free trial. Subscribe to a plan to continue.'
        : 'You need an active subscription to use this tool.';

  const handleClose = () => setGateModalOpen(false);

  const handleGoToPricing = () => {
    setGateModalOpen(false);
    navigate('/pricing');
  };

  return (
    <Dialog
      open={isGateModalOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      data-testid="access-blocked-modal"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOutlinedIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {body}
        </Typography>
        {trialState && (
          <Typography variant="body2" color="text.secondary">
            Used ${trialState.spentUsd.toFixed(2)}
            {trialState.capUsd !== null && ` of $${trialState.capUsd.toFixed(2)}`}
            {trialState.daysLeft !== undefined && trialState.status === 'trialing' &&
              ` · ${trialState.daysLeft} day${trialState.daysLeft === 1 ? '' : 's'} left in trial`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Not now
        </Button>
        <Button onClick={handleGoToPricing} variant="contained">
          Choose a plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessBlockedModal;
